import { Component, SecurityContext, OnInit, ViewEncapsulation, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';

import { NavController, Platform, ModalController, IonInfiniteScroll } from '@ionic/angular';
import { AppState, AuthService, EventService, SharedService, MyPenmateService } from '../services';
import { Store } from '@ngrx/store';
import { UserActions, CreateMessageActions, MyPenmateActions } from '../actions';
import { orderBy, isEmpty, get, chain } from 'lodash';
import * as moment from 'moment';

import { MessageTextEditor } from '../message-text-editor/message-text-editor';
import { MyPenmatesPage } from '../my-penmates/my-penmates';
import { MessageShowPage } from '../message-show/message-show';
import { LoginPage } from '../login/login.page';
declare var Beacon;

import { Observable } from 'rxjs';

@Component({
  selector: 'pm-page-message-index',
  templateUrl: 'message-index.html',
  styleUrls: ['./message-index.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MessageIndexPage implements OnInit {
  @ViewChild(IonInfiniteScroll, { static: false }) infiniteScroll: IonInfiniteScroll;
  pageHeight;
  penmate;
  modal;
  messages;
  onGoBackFn;
  isLoading = false;
  nextCursor: string = null;
  isInitialLoad = true;

  constructor(
    public navCtrl: NavController,
    public createMessageActions: CreateMessageActions,
    public myPenmateActions: MyPenmateActions,
    public userActions: UserActions,
    public authService: AuthService,
    public sharedService: SharedService,
    public eventService: EventService,
    private myPenmateService: MyPenmateService,
    private sanitizer: DomSanitizer,
    private platform: Platform,
    private store: Store<AppState>,
    private modalCtrl: ModalController,
    private route: ActivatedRoute,
  ) {
    this.pageHeight = platform.height();
    platform.ready().then(() => {
      this.store.dispatch(this.myPenmateActions.loadPenmates());
    });
  }

  mapMessages(messageArray) {
    const allSent = messageArray.filter((m) =>
      get(m, 'status', '').match(/processing|ready_to_be_mailed|mailed/i),
    );
    const sent = orderBy(
      allSent,
      [
        (o) => {
          return moment(o.timestamp).unix();
        },
        (o) => {
          return moment(o.send_date).unix();
        },
      ],
      'desc',
    );
    const draft = messageArray.filter((m) => get(m, 'status', '').match(/draft/i));
    const emessages = messageArray.filter((m) => m.message_type === 'emessage');
    const messages = emessages.concat(sent).concat(draft);
    const allMessages = (this.messages || []).concat(orderBy(
     messages,
      [
        (o) => {
          if (get(o, 'status', '').match(/draft/i)) {
            return moment(o.timestamp).unix();
          }
          return moment(o.timestamp).unix();
        },
        (o) => {
          return o.send_date ? moment(o.send_date).unix() : moment(o.timestamp).unix();
        },
      ],
      'desc',
    ));

    const mergedMessages = chain(allMessages)
      .groupBy('id')
      // Prefer the newest server payload for duplicate IDs.
      // `allMessages` is built as: existing messages + newly fetched messages, so the last
      // occurrence in each group represents the freshest server payload.
      .map(objs => objs[objs.length - 1])
      .orderBy(
        [
          (o) => {
            if (get(o, 'status', '').match(/draft/i)) {
              return moment(o.timestamp).unix();
            }
            return moment(o.timestamp).unix();
          },
          (o) => {
            return o.send_date ? moment(o.send_date).unix() : moment(o.timestamp).unix();
          },
        ],
        'desc',
      )
      .value();
    this.messages = mergedMessages;
    this.isLoading = false;
  }

  ngOnInit() {
    this.store
    .select((state) => state.myPenmates.currentPenmate)
    .subscribe((penmate) => {
      const activePenmateId = this.route.snapshot.paramMap.get('id');
      if (activePenmateId && penmate && String(penmate.id) !== String(activePenmateId)) {
        return;
      }
      this.penmate = penmate;
      if (penmate && penmate.messages) {
        const allSent = penmate.messages.filter((m) =>
          get(m, 'status', '').match(/processing|ready_to_be_mailed|mailed/i),
        );
        this.nextCursor = get(penmate, 'next_cursor', null);
        const sent = orderBy(
          allSent,
          [
            (o) => {
              return moment(o.timestamp).unix();
            },
            (o) => {
              return moment(o.send_date).unix();
            },
          ],
          'desc',
        );
        const lastSentMessage = sent[0];
        this.mapMessages(penmate.messages);
        const confirmedToAddress = get(lastSentMessage || {}, 'confirmed_address.toAddress')
        if (confirmedToAddress) {
          this.penmate.facility = confirmedToAddress.facility || this.penmate.facility
          this.penmate.first_name = confirmedToAddress.first_name || this.penmate.first_name
          this.penmate.last_name = confirmedToAddress.last_name || this.penmate.last_name
        }
      } else {
        this.isLoading = false;
      }
    });
    this.sharedService.data.subscribe(({ event }) => {
      if (event.type === 'onGoBack') {
        this.onGoBackFn = get(event, 'data.onGoBack');
      }
    });
  }

  ionViewDidEnter() {
    if (typeof Beacon === 'function') {
      Beacon('destroy');
    }
  }

  ionViewWillEnter() {
    if (this.modal) {
      this.modal.dismiss();
    }
    this.nextCursor = null;
    this.isInitialLoad = true;
    const penmateId = this.route.snapshot.paramMap.get('id');
    if (!this.penmate || String(this.penmate.id) !== String(penmateId)) {
      this.messages = [];
    }
    this.isLoading = true;
    this.store.dispatch(this.myPenmateActions.loadMessages(penmateId));
  }

  onFetchMessages(cursor = undefined) {
    return new Observable(observer => {
      if (!this.penmate || !this.penmate.id) {
        this.isLoading = false;
        observer.next([]);
        return observer.complete();
      }
      this.myPenmateService.loadMessages(this.penmate.id, cursor).subscribe((res) => {
        const messages = [...get(res, 'letters', []), ...get(res, 'emessages', [])]
        const sentMessages = messages.map(m => {
          let msg = Object.assign({}, m.data, m); // copy data key to top-level
          return msg;
        });
        this.mapMessages(sentMessages);
        if (res.penmate) {
          this.store.dispatch(this.myPenmateActions.viewPenmate(res.penmate));
        }
        this.store.dispatch(this.myPenmateActions.loadMessagesSuccess({
          messages,
          next_cursor: res.next_cursor,
          has_next_page: res.has_next_page
        }) );
        this.isLoading = false;
        this.nextCursor = res.next_cursor || null;
        observer.next(res)
        observer.complete();
      })
    });

  }

  loadNextPage(event) {
    if (this.penmate.id) {
      if (!this.nextCursor) {
        event.target.complete();
        event.target.disabled = true;
        return;
      }
      this.onFetchMessages(this.nextCursor).subscribe((res: any) => {
        this.nextCursor = res.next_cursor || null;
        event.target.complete();
        event.target.disabled = !res.has_next_page;
      })
    }
  }

  public trackItem (_index, item) {
    return item.id;
  }
  

  onShowMessage(message) {
    message.sent || message.processing || message.message_type === 'emessage'
      ? this.showSentMessage(message)
      : this.showEditMessage(message);
  }

  showSentMessage(message) {
    const isEmessage = message.message_type === 'emessage' 
    this.navCtrl.navigateForward(`/penmates/${this.penmate.id}/messages/${message.id}?emessage=${isEmessage}`);
    this.eventService.track('show-message', { [isEmessage ? 'message_id' : 'letter_id']: message.id, penmate_id: this.penmate.id, emessage: isEmessage });
  }

  showEditMessage = async (message) => {
    this.store.dispatch(this.createMessageActions.setMessage(message));
    this.modal = await this.modalCtrl.create({ component: MessageTextEditor });
    this.eventService.track('edit-message', { letter_id: message.id, penmate_id: this.penmate.id });
    this.navCtrl.navigateForward(`/penmates/${this.penmate.id}/new-message`);
  };

  onCancel() {
    this.navCtrl.pop();
  }

  onGoBack = () => {
    if (this.onGoBackFn) {
      return this.onGoBackFn();
    }
    this.navCtrl.navigateForward('/my-penmates');
  };

  sanitizeHtml(html) {
    const formattedHtml = isEmpty(html) ? 'No text included' : html;
    return this.sanitizer.bypassSecurityTrustHtml(formattedHtml);
  }

  // Helper method to check if a date is within 1 week
  isWithinOneWeek(date: string | Date): boolean {
    if (!date) return false;
    const now = moment();
    const messageDate = moment(date);
    const diffInDays = now.diff(messageDate, 'days');
    return diffInDays <= 7;
  }
}
